//@flow
import React, { useState, useCallback, useMemo } from "react";
import { type History } from "history";
import { useArrowNavigationTransmitter } from "hooks";
import { postMessageTo } from "hooks/PostMessage/Utils";
import { ArrowNavigationAction } from "hooks/PostMessage/Actions";
import { getDisplayName } from "data/constants";
import { ArrowNavigationContext } from "data/context";
import { type ArrowNavigationSetter } from "data/types";

type Config = {
    searchParamName: string,
};

type Props = {
    history: History,
};

export const withArrowNavigation =
    ({ qsParam }: Config) =>
    (WrappedComponent: ComponentType<any>) => {
        const HOC = (props: Props) => {
            const [source, setSource] = useState(undefined);
            const [target, setTarget] = useState(undefined);
            const [rows, setRows] = useState([]);
            const [currentIndex, setCurrentIndex] = useState(
                props?.activeRowIndex ?? -1
            );

            const handleSetRows = useCallback(
                (rows) => {
                    postMessageTo(
                        ArrowNavigationAction.Reload,
                        {
                            source,
                            rows,
                            currentIndex,
                        },
                        target
                    );
                    setRows(rows);
                },
                [source, target, currentIndex]
            );

            const handleSetCurrentIndex = useCallback(
                (rowIndex) => {
                    /* istanbul ignore else */
                    if (rowIndex > -1 && rowIndex !== currentIndex) {
                        postMessageTo(
                            ArrowNavigationAction.SetIndex,
                            { source, currentIndex: rowIndex },
                            target
                        );
                    }
                    return setCurrentIndex(rowIndex);
                },
                [currentIndex, source, target]
            );

            const context: ArrowNavigationSetter = useMemo(
                () => ({
                    setSource,
                    setTarget,
                    setRows: handleSetRows,
                    setCurrentIndex: handleSetCurrentIndex,
                }),
                [handleSetRows, handleSetCurrentIndex]
            );

            const targetData = useMemo(
                () => ({
                    source,
                    rows,
                    currentIndex,
                    qsParam,
                }),
                [source, rows, currentIndex]
            );
            useArrowNavigationTransmitter(
                source,
                target,
                targetData,
                setCurrentIndex,
                props.history
            );
            return (
                <ArrowNavigationContext.Provider value={context}>
                    <WrappedComponent {...props} postMessage={context} />
                </ArrowNavigationContext.Provider>
            );
        };
        HOC.displayName = `withArrowNavigation(${getDisplayName(
            WrappedComponent
        )})`;
        return HOC;
    };

export default withArrowNavigation;
